"use client"

import { useState } from "react"
import Link from "next/link"
import { LogIn } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Kbd } from "@/components/ui/kbd"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { SidebarTrigger, useSidebar } from "@/components/ui/custom-sidebar"
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog"
import { UserButton } from "@/components/nav/UserButton"
import { ThemeToggle } from "@/components/theme/theme-toggle"
import { PAGE_HEADER_SLOT_ID } from "@/components/layout/page-slots"
import { useAuth } from "@/context/AuthContext"

/**
 * The app's one header. Every route wears it — the research canvas, the reader,
 * the marketing pages, the auth pages — because the alternative, which this
 * replaces, was three headers that drifted: a marketing SiteHeader, the shell's
 * own bar, and a set of controls the research canvas drew for itself.
 *
 * Fixed and transparent, with content running underneath and dissolving into it —
 * see CHROME_MASK in lib/fade.ts. It used to raise a shadow once the scrollport
 * moved; the fade replaces that, and does it without a scroll listener.
 *
 * Three zones, left to right:
 *   left    sidebar toggle + wordmark
 *   middle  PAGE_HEADER_SLOT — whichever page is mounted portals its controls here
 *   right   command menu, feedback, theme, account (or the signed-out CTAs)
 *
 * The middle zone is a portal target rather than a prop because the layout renders
 * one header for every route and can't know which page is below it. See
 * components/layout/page-slots.tsx.
 */
export function AppHeader({
  /**
   * `minimal` drops the sidebar toggle, the page slot and the app tools — for
   * sign-in and sign-up, where the only thing on offer is getting back to the app.
   */
  variant = "app",
  className,
}: {
  variant?: "app" | "minimal"
  className?: string
}) {
  const [commandOpen, setCommandOpen] = useState(false)
  const { user } = useAuth()
  const { open } = useSidebar()

  const isApp = variant === "app"

  return (
    <>
      <header
        className={cn(
          "fixed inset-x-0 top-0 z-30 h-16 flex flex-row items-center gap-6 px-4 md:px-8",
          className,
        )}
      >
        <div className="flex shrink-0 flex-row items-center gap-3">
          {/* {isApp && (
            <Tooltip>
              <TooltipTrigger asChild>
                <SidebarTrigger variant="secondary" size="icon" />
              </TooltipTrigger>
              <TooltipContent>
                {open ? "Hide" : "Show"} sidebar <Kbd>⌘ b</Kbd>
              </TooltipContent>
            </Tooltip>
          )} */}

          <Link
            href="/"
            className="text-2xl font-semibold leading-none whitespace-nowrap"
          >
            LawLibrary<span className="text-primary">.ai</span>
          </Link>
        </div>

        {/*
            Page controls. `min-w-0` + horizontal scroll so a toolbar-heavy page
            (the reader has a dozen toggles) shrinks rather than pushing the
            account button off the right edge on a narrow window.

            RIGHT ALIGNED, which a page's controls have to be laid out for: the
            row's right edge is what stays put, so a control holds its position
            only while everything that appears and disappears is to its LEFT.
            Anything that decides whether another control exists therefore goes
            to the RIGHT of what it reveals — otherwise pressing it removes a
            neighbour and slides the control itself out from under the pointer,
            and a second press lands on whatever moved into its place. */}
        {isApp && (
          <div
            id={PAGE_HEADER_SLOT_ID}
            className="ml-auto py-4 flex min-w-0 flex-row items-center justify-end gap-6 overflow-x-auto [scrollbar-width:none]"
          />
        )}

        <div
          className={cn(
            "flex shrink-0 flex-row items-center gap-2",
            !isApp && "ml-auto",
          )}
        >
          <ThemeToggle size="icon" />

          {isApp && (
            <FeedbackDialog />
          )}

          {user ? (
            <UserButton />
          ) : (
            // No free accounts, so the primary CTA is the plans page, not sign-up:
            // an account only exists on the other side of a subscription.
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="secondary" size="icon" asChild className="hidden sm:inline-flex">
                    <Link href="/sign-in">
                      <LogIn />
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Sign in
                </TooltipContent>
              </Tooltip>


              <Button size="default" asChild>
                <Link href="/pricing">Subscribe</Link>
              </Button>
            </>
          )}
        </div>
      </header>
    </>
  )
}
